import { PERIOD, priceY, yPrice } from './chart-data';
import type { Candle, Scale, Anchor } from './chart-data';
import type { Timeframe } from './zone-math';

export type InkTool = 'pen' | 'highlight' | 'line' | 'arrow' | 'rectangle' | 'ellipse';
export type InkStroke = { id: string; scope: string; tool: InkTool; color: string; width: number; points: Anchor[]; createdAt: number; updatedAt: number; deleted: boolean };
export type InkChange = { stroke: InkStroke; before: boolean; after: boolean };
export type InkViewport = { rows: Candle[]; scale: Scale; left: number; right: number; candleRight: number; timeframe: Timeframe };
export type XY = { x: number; y: number };

// Fractional candle coordinates keep freehand lines smooth, including across
// weekend gaps. Price/time anchors follow pan, zoom and viewport resizing.
export function inkAnchor(p: XY, v: InkViewport): Anchor {
  const step = (v.candleRight - v.left) / v.rows.length;
  const at = (p.x - v.left) / step - .5, index = Math.floor(at);
  let time: number;
  if (index < 0) time = v.rows[0].time + at * PERIOD[v.timeframe];
  else if (index >= v.rows.length - 1) time = v.rows.at(-1)!.time + (at - v.rows.length + 1) * PERIOD[v.timeframe];
  else time = v.rows[index].time + (at - index) * (v.rows[index + 1].time - v.rows[index].time);
  return { time, price: yPrice(p.y, v.scale) };
}
export function inkPoint(p: Anchor, v: InkViewport): XY {
  let at: number;
  if (p.time < v.rows[0].time) at = (p.time - v.rows[0].time) / PERIOD[v.timeframe];
  else if (p.time >= v.rows.at(-1)!.time) at = v.rows.length - 1 + (p.time - v.rows.at(-1)!.time) / PERIOD[v.timeframe];
  else {
    let low = 0, high = v.rows.length - 1;
    while (low + 1 < high) { const mid = (low + high) >> 1; if (v.rows[mid].time <= p.time) low = mid; else high = mid; }
    at = low + (p.time - v.rows[low].time) / (v.rows[low + 1].time - v.rows[low].time);
  }
  return { x: v.left + (at + .5) * (v.candleRight - v.left) / v.rows.length, y: priceY(p.price, v.scale) };
}
export function inkOutline(stroke: InkStroke, v: InkViewport): XY[] {
  const points = stroke.points.map(p => inkPoint(p, v)), a = points[0], b = points.at(-1)!;
  if (stroke.tool === 'rectangle') return [a, { x:b.x, y:a.y }, b, { x:a.x, y:b.y }, a];
  if (stroke.tool === 'ellipse') return Array.from({ length:65 }, (_, i) => ({ x:(a.x+b.x)/2 + Math.abs(b.x-a.x)/2*Math.cos(i*Math.PI/32), y:(a.y+b.y)/2 + Math.abs(b.y-a.y)/2*Math.sin(i*Math.PI/32) }));
  if (stroke.tool === 'arrow') {
    const angle = Math.atan2(b.y-a.y,b.x-a.x), length = Math.max(12,stroke.width*4);
    return [a,b,{x:b.x-length*Math.cos(angle-.5),y:b.y-length*Math.sin(angle-.5)},b,{x:b.x-length*Math.cos(angle+.5),y:b.y-length*Math.sin(angle+.5)}];
  }
  return points;
}
export function inkPath(points: XY[]) { return points.map((p,i)=>(i?'L':'M')+p.x.toFixed(2)+' '+p.y.toFixed(2)).join(' ') + (points.length === 1 ? ' l .01 0' : ''); }
function distance(p: XY, a: XY, b: XY) {
  const dx=b.x-a.x, dy=b.y-a.y, length=dx*dx+dy*dy;
  const t=length ? Math.max(0,Math.min(1,((p.x-a.x)*dx+(p.y-a.y)*dy)/length)) : 0;
  return Math.hypot(p.x-a.x-t*dx,p.y-a.y-t*dy);
}
function cross(a: XY,b: XY,c: XY) { return (b.x-a.x)*(c.y-a.y)-(b.y-a.y)*(c.x-a.x); }
export function inkHit(stroke: InkStroke, v: InkViewport, from: XY, to: XY, radius = 10) {
  const points=inkOutline(stroke,v), tolerance=radius+stroke.width*(stroke.tool==='highlight'?2.5:.5);
  if(points.length===1)return distance(points[0],from,to)<=tolerance;
  return points.slice(1).some((b,i)=>{
    const a=points[i];
    const intersects=cross(from,to,a)*cross(from,to,b)<0 && cross(a,b,from)*cross(a,b,to)<0;
    return intersects || Math.min(distance(a,from,to),distance(b,from,to),distance(from,a,b),distance(to,a,b))<=tolerance;
  });
}
export function mergeInk(current: InkStroke[], incoming: InkStroke[]) {
  const map=new Map(current.map(row=>[row.id,row]));
  for(const row of incoming)if(!map.has(row.id)||row.updatedAt>=map.get(row.id)!.updatedAt)map.set(row.id,row);
  return [...map.values()].sort((a,b)=>a.createdAt-b.createdAt||a.id.localeCompare(b.id));
}
async function openInk(): Promise<IDBDatabase> {
  return new Promise((resolve,reject)=>{
    if(typeof indexedDB==='undefined'){reject(new Error('Storan tidak tersedia'));return;}
    const req=indexedDB.open('zonepips-chart-ink',1);
    req.onupgradeneeded=()=>req.result.createObjectStore('strokes',{keyPath:'id'}).createIndex('scope','scope');
    req.onsuccess=()=>resolve(req.result);req.onerror=()=>reject(req.error);req.onblocked=()=>reject(new Error('Storan sibuk'));
  });
}
export async function readInk(scope: string): Promise<InkStroke[]> {
  const db=await openInk();
  return new Promise((resolve,reject)=>{
    const tx=db.transaction('strokes','readonly'), req=tx.objectStore('strokes').index('scope').getAll(scope);
    tx.oncomplete=()=>{db.close();resolve(req.result);};tx.onerror=tx.onabort=()=>{db.close();reject(tx.error);};
  });
}
// Update individual IDs, never replace another tab's entire collection.
export async function writeInk(changes: InkChange[], undo = false): Promise<InkStroke[]> {
  const db=await openInk();
  return new Promise((resolve,reject)=>{
    const tx=db.transaction('strokes','readwrite'), store=tx.objectStore('strokes'), result:InkStroke[]=[];
    for(const change of changes){
      const req=store.get(change.stroke.id);
      req.onsuccess=()=>{
        const old=(req.result as InkStroke)||change.stroke;
        const row={...old,deleted:undo?change.before:change.after,updatedAt:Math.max(Date.now(),old.updatedAt+1)};
        store.put(row);result.push(row);
      };
    }
    tx.oncomplete=()=>{db.close();resolve(result);};tx.onerror=tx.onabort=()=>{db.close();reject(tx.error);};
  });
}
